import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { queries } from "../lib/api";
import { useDocumentTitle } from "../lib/navigation";
import { styles } from "../lib/styles";
import { PageHeader, ResultStatus, SectionTabs } from "../components/Workspace";
import { RequestFailure } from "../components/RequestFailure";
export const Route = createFileRoute("/connect")({ component: ConnectPage });
function ConnectPage() {
  useDocumentTitle("Connect from your device");
  const aliases = useQuery(queries.aliases());
  const [platform, setPlatform] = useState<"windows" | "linux" | "headless">("windows");
  const [copied, setCopied] = useState("");
  const origin = window.location.origin;
  const command =
    platform === "windows"
      ? `irm ${origin}/install.ps1 | iex`
      : `curl -fsSL ${origin}/install.sh -o /tmp/ssbd-access-install.sh\nsh /tmp/ssbd-access-install.sh --${platform === "linux" ? "desktop" : "headless"}`;
  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(`${label} copied.`);
    } catch {
      setCopied("Clipboard is unavailable. Select the command text and copy it manually.");
    }
  };
  return (
    <>
      <PageHeader
        title="Connect from your device"
        description="Install SSBD Access on Windows or Linux AMD64, sign in and use ordinary SSH from your terminal, editor or AI agent."
      />
      <div {...stylex.props(styles.grid)}>
        <section data-connect-instructions data-workspace-panel {...stylex.props(styles.panel)}>
          <h2 {...stylex.props(styles.panelHeading)}>1. Connect your device to NetBird</h2>
          <p {...stylex.props(styles.description)}>
            Install the NetBird client and sign in to your company network. Use the management
            server supplied by your administrator.
          </p>
          <div {...stylex.props(styles.actions)}>
            <a
              href="https://docs.netbird.io/get-started/install"
              target="_blank"
              rel="noopener noreferrer"
            >
              NetBird installation guide
            </a>
          </div>
          <h2 {...stylex.props(styles.panelHeading)}>2. Install and open SSBD Access</h2>
          <SectionTabs
            value={platform}
            onChange={(value) => {
              setPlatform(value);
              setCopied("");
            }}
            tabs={[
              { id: "windows", label: "Windows" },
              { id: "linux", label: "Linux desktop" },
              { id: "headless", label: "Linux server" },
            ]}
          />
          <p {...stylex.props(styles.description)}>
            {platform === "windows"
              ? "Open PowerShell in your user account, paste this command and press Enter. The installer opens the desktop app when installation finishes."
              : platform === "linux"
                ? "Open a terminal in your desktop session and run these commands as your user. The installer opens the desktop app."
                : "Open an SSH terminal on the server and run these commands as your user. The headless client starts in that terminal and must remain running."}
          </p>
          <pre {...stylex.props(styles.code)}>{command}</pre>
          <Button
            label="Copy install command"
            onClick={() => {
              void copy(command, "Install command");
            }}
          />
          <p {...stylex.props(styles.small)}>
            If an older SSBD Access instance is running, stop it before reinstalling. The installer
            preserves your personal key and SSH aliases.
          </p>
          <h2 {...stylex.props(styles.panelHeading)}>3. Approve access and connect</h2>
          <p {...stylex.props(styles.description)}>
            {platform === "windows" || platform === "linux"
              ? "In the desktop app, select Connect securely and approve the matching code in your browser. Keep the app in the tray while you work."
              : "Follow the sign-in link printed by the terminal client and approve its matching code. If you need to start it again, use:"}
          </p>
          {platform === "headless" && (
            <pre {...stylex.props(styles.code)}>ssbi-ssh headless --url {origin}</pre>
          )}
          <p {...stylex.props(styles.description)}>
            Use an alias from the list to connect. Stop or Quit closes managed sessions; an abrupt
            stop is detected within about 15 seconds. Your private key stays on your device.
          </p>
          <details>
            <summary>Connection checks and script downloads</summary>
            <pre {...stylex.props(styles.code)}>
              ssbi-ssh status{"\n"}ssbi-ssh doctor{"\n"}ssbi-ssh sync
            </pre>
            <p {...stylex.props(styles.small)}>
              Run doctor if NetBird or SSH is unavailable; sync refreshes aliases. Personal alias
              conflicts are preserved and explained by the client.
            </p>
            <div {...stylex.props(styles.actions)}>
              <a href="/install.ps1" download>
                Download PowerShell script
              </a>
              <a href="/install.sh" download>
                Download shell script
              </a>
              <a href="/downloads/checksums.json">SHA256 checksums</a>
            </div>
          </details>
        </section>
        <section data-workspace-panel {...stylex.props(styles.panel)}>
          <h2 {...stylex.props(styles.panelHeading)}>Your available aliases</h2>
          <RequestFailure
            error={aliases.error}
            retry={() => {
              void aliases.refetch();
            }}
            pending={aliases.isFetching}
          />
          {aliases.isPending && <ResultStatus>Loading your available SSH aliases…</ResultStatus>}
          {aliases.data && (
            <ResultStatus>
              {aliases.data.length} SSH {aliases.data.length === 1 ? "alias" : "aliases"} available.
            </ResultStatus>
          )}
          <section className="connect-alias-list" aria-label="Available SSH aliases">
            {aliases.data?.map((alias) => (
              <div key={alias.alias}>
                <pre {...stylex.props(styles.code)}>ssh {alias.alias}</pre>
                <p {...stylex.props(styles.small)}>
                  {alias.login}@{alias.address}:{alias.port}
                </p>
                <Button
                  label={`Copy SSH command for ${alias.alias}`}
                  onClick={() => {
                    void copy(`ssh ${alias.alias}`, "SSH command");
                  }}
                />
              </div>
            ))}
          </section>
          {aliases.data?.length === 0 && (
            <p {...stylex.props(styles.empty)}>
              No applied access grants yet. Ask your administrator to check your SSH group and the
              server's synchronization status.
            </p>
          )}
        </section>
      </div>
      {copied && <ResultStatus>{copied}</ResultStatus>}
    </>
  );
}
